import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = [
    'JavaScript (ES6+)',
    'TypeScript',
    'Python',
    'Node.js & Express',
    'FastAPI',
    'React & Next.js',
    'PostgreSQL & MongoDB',
    'Redis',
    'Hugging Face & LangChain',
    'Docker',
  ];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              {/* Hello! My name is Khizar, and I enjoy building systems that power modern web and
              AI-driven applications. My journey into development started during my computer science
              degree, where I became fascinated with how scalable systems work behind the scenes.
              What began with building full-stack web projects gradually evolved into designing
              production-ready APIs, real-time architectures, and subscription-based platforms.
              Along the way, solving 250+ data structures and algorithms problems strengthened how I
              approach performance, scalability, and edge cases in real-world systems. */}
              Hello! My name is Khizar, and I enjoy building systems that power modern web and
              AI-driven applications. My journey into development started during my computer science
              degree, where I became fascinated with how scalable systems work behind the scenes.
              What began with building full-stack web projects gradually evolved into designing
              production-ready REST APIs with Node.js and FastAPI, implementing WebSocket-based
              real-time architectures, integrating Stripe subscription workflows, and designing
              relational and NoSQL databases. Along the way, solving 300+ data structures and
              algorithms problems strengthened how I approach performance, scalability, and edge
              cases in real-world systems.
            </p>

            {/* <p> */}
            {/* Fast-forward to today, and I’ve had the privilege of working at{' '}
              <a href="https://us.mullenlowe.com/">an advertising agency</a>,{' '}
              <a href="https://starry.com/">a start-up</a>,{' '}
              <a href="https://www.apple.com/">a huge corporation</a>, and{' '}
              <a href="https://scout.camd.northeastern.edu/">a student-led design studio</a>. My
              main focus these days is building accessible, inclusive products and digital
              experiences at <a href="https://upstatement.com/">Upstatement</a> for a variety of
              clients. */}
            {/* Fast-forward to today, I’ve worked as a Software Engineer building scalable REST APIs
              with Node.js and FastAPI, implementing WebSocket-based real-time communication,
              integrating Stripe subscription workflows, and designing relational and NoSQL
              databases. I’ve deployed Dockerized services, handled background processing with Redis
              and Celery, and contributed to production systems used by real users. */}
            {/* </p> */}

            <p>
              {/* I also recently{' '}
              <a href="https://www.newline.co/courses/build-a-spotify-connected-app">
                launched a course
              </a>{' '}
              that covers everything you need to build a web app with the Spotify API using Node
              &amp; React. */}
              Recently, my focus has shifted toward Applied AI engineering. I’ve built a
              domain-restricted RAG system using Hugging Face models, sentence-transformer
              embeddings, and ChromaDB — implementing similarity-based retrieval to ensure grounded,
              reliable responses. I’m particularly interested in building AI-driven products that
              combine strong backend architecture with modern LLM capabilities.
            </p>

            <p>Here are a few technologies I’ve been working with recently:</p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
